import { Notice, Section } from "@/components/Page.js"
import { Button } from "@/components/ui/button.js"
import { Input } from "@/components/ui/input.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import type { ConfigChange } from "./diff.js"
import type { Draft } from "./draft.js"

/**
 * Publishing, and **what one publish would change, before it changes it.**
 *
 * A change takes effect on the next decision with no cache to purge, so the
 * screen behaves like it: the list names every edit, the button refuses when
 * there are none, and a version that landed underneath the draft says so rather
 * than quietly winning or quietly losing.
 */
export function PublishPanel({ draft, version }: { draft: Draft; version: number | null }) {
  return (
    <Section title="Publikacja" hint="Zmiana działa od następnej decyzji.">
      <div className="space-y-3">
        {draft.supersededBy !== null && (
          <Notice tone="critical">
            W trakcie edycji opublikowano wersję{" "}
            <span className="font-mono">{draft.supersededBy}</span>. Poniżej jest to, co twoja
            publikacja zmieni względem niej.{" "}
            <button type="button" className="underline underline-offset-2" onClick={draft.discard}>
              Odrzuć moje zmiany
            </button>
          </Notice>
        )}

        <ChangeList changes={draft.changes} />

        <div className="flex max-w-xl flex-wrap gap-2.5">
          <Input
            className="min-w-64 flex-1"
            placeholder="Co się zmieniło i dlaczego"
            aria-label="Notatka do wersji"
            value={draft.note}
            onChange={(event) => draft.setNote(event.target.value)}
          />
          <Button onClick={draft.publish} disabled={draft.publishing || draft.changes.length === 0}>
            {draft.publishing ? "Publikuję…" : "Opublikuj"}
          </Button>
        </div>

        {draft.error !== null && draft.error !== undefined && (
          <p className="text-sm text-critical">
            {draft.error instanceof Error ? draft.error.message : "Nie udało się opublikować."}
          </p>
        )}

        {version === null && (
          <p className="text-[13px] text-muted-foreground">
            Nic jeszcze nie opublikowano — serwis działa na wbudowanych domyślnych.
          </p>
        )}
      </div>
    </Section>
  )
}

export function ChangeList({ changes }: { changes: readonly ConfigChange[] }) {
  if (changes.length === 0) {
    return <p className="text-sm text-muted-foreground">Bez zmian względem wersji na żywo.</p>
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Gdzie</Th>
          <Th>Ustawienie</Th>
          <Th>Z</Th>
          <Th>Na</Th>
        </tr>
      </thead>
      <tbody>
        {changes.map((change) => (
          <Tr key={`${change.scope}·${change.label}`}>
            <Td className="whitespace-nowrap text-muted-foreground">{change.scope}</Td>
            <Td>{change.label}</Td>
            <Td className="whitespace-nowrap text-muted-foreground line-through">{change.from}</Td>
            <Td className="whitespace-nowrap font-medium">{change.to}</Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  )
}
